import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { api } from '../../lib/api';

interface DailyPoint {
  date: string;
  count: number;
}

interface StatusBreakdown {
  status: string;
  count: number;
}

interface TypeBreakdown {
  type: string;
  total: number;
  sent: number;
  failed: number;
  skipped: number;
}

interface PushHealthDetails {
  rangeDays: number;
  totals: {
    sent: number;
    failed: number;
    queued: number;
    skippedQuiet: number;
    skippedPrefs: number;
    skippedMaster: number;
    /** Lern-Push an einem Pausentag unterdrückt (CUR-1724); fehlt bei älteren Backends. */
    skippedPause?: number;
    noTokens: number;
  };
  byStatus: StatusBreakdown[];
  byType: TypeBreakdown[];
  dailySent: DailyPoint[];
  dailyFailed: DailyPoint[];
  topFailureReasons: { reason: string; count: number }[];
  tokens: {
    total: number;
    iosCount: number;
    androidCount: number;
    activeUserCount: number;
  };
  generatedAt: string;
}

function fetchPushDetails(rangeDays = 30): Promise<PushHealthDetails> {
  return api.get<PushHealthDetails>(`/admin/health/push/details?days=${rangeDays}`);
}

export function PushDetailsSection(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['health', 'push-details'],
    queryFn: () => fetchPushDetails(30),
    refetchInterval: 5 * 60_000,
  });

  if (isLoading) return <Box>Lädt …</Box>;
  if (error) {
    return (
      <Box dangerous>
        Fehler: {error instanceof Error ? error.message : 'unbekannt'}
      </Box>
    );
  }
  if (!data) return <Box>Keine Daten.</Box>;

  const totalSends = data.totals.sent + data.totals.failed + data.totals.queued;
  const totalSkips =
    data.totals.skippedQuiet +
    data.totals.skippedPrefs +
    data.totals.skippedMaster +
    (data.totals.skippedPause ?? 0);
  const failureRate = totalSends > 0 ? data.totals.failed / totalSends : 0;
  const failureSeverity: 'ok' | 'warn' | 'crit' =
    failureRate > 0.1 ? 'crit' : failureRate > 0.03 ? 'warn' : 'ok';

  return (
    <article
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">Push — Details</h3>
        <span className="text-[11px] text-text-tertiary">
          {totalSends.toLocaleString('de-DE')} Sends · {totalSkips.toLocaleString('de-DE')} Skips ·
          letzte {data.rangeDays} Tage
        </span>
      </header>

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Aktive Tokens" value={data.tokens.total.toLocaleString('de-DE')} />
        <Stat
          label="Geräte"
          value={`${data.tokens.iosCount} iOS · ${data.tokens.androidCount} Android`}
        />
        <Stat
          label="Aktive User"
          value={data.tokens.activeUserCount.toLocaleString('de-DE')}
        />
        <Stat
          label="Failure-Rate"
          value={`${(failureRate * 100).toFixed(1)} %`}
          severity={failureSeverity}
        />
      </div>

      <section className="mb-4">
        <SectionTitle>Sends pro Tag (letzte {data.rangeDays} Tage)</SectionTitle>
        <DailyBars sent={data.dailySent} failed={data.dailyFailed} />
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <TypeBreakdownTable rows={data.byType} />
        <SkipsBreakdown totals={data.totals} />
      </div>

      {data.topFailureReasons.length > 0 && (
        <section className="mt-4">
          <SectionTitle>Top Failure-Reasons</SectionTitle>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-text-tertiary">
                <th className="pb-1 font-normal">Reason</th>
                <th className="pb-1 text-right font-normal">Anzahl</th>
              </tr>
            </thead>
            <tbody>
              {data.topFailureReasons.map((r) => (
                <tr key={r.reason}>
                  <td className="py-0.5 font-mono text-text-primary">{r.reason}</td>
                  <td className="py-0.5 text-right tabular-nums text-text-primary">
                    {r.count.toLocaleString('de-DE')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </article>
  );
}

function Stat({
  label,
  value,
  severity,
}: {
  label: string;
  value: string;
  severity?: 'ok' | 'warn' | 'crit';
}): JSX.Element {
  const color =
    severity === 'crit'
      ? darkTheme.colors.danger
      : severity === 'warn'
        ? darkTheme.colors.warning
        : darkTheme.colors.textPrimary;
  return (
    <div className="rounded border p-2.5" style={{ borderColor: darkTheme.colors.borderSubtle }}>
      <div className="text-[10px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-1 font-semibold tabular-nums" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
      {children}
    </h4>
  );
}

function DailyBars({
  sent,
  failed,
}: {
  sent: DailyPoint[];
  failed: DailyPoint[];
}): JSX.Element {
  // Daten zu einer Date-Reihe mergen.
  const byDate = new Map<string, { sent: number; failed: number }>();
  for (const p of sent) {
    byDate.set(p.date, { sent: p.count, failed: 0 });
  }
  for (const p of failed) {
    const entry = byDate.get(p.date) ?? { sent: 0, failed: 0 };
    entry.failed = p.count;
    byDate.set(p.date, entry);
  }
  const points = Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, v]) => ({ date, ...v }));

  if (points.length === 0) {
    return <div className="text-xs text-text-tertiary">Keine Daten.</div>;
  }

  const max = Math.max(...points.map((p) => p.sent + p.failed), 1);

  return (
    <div className="flex h-24 items-end gap-0.5">
      {points.map((p) => {
        const totalH = ((p.sent + p.failed) / max) * 100;
        const failedH = totalH > 0 ? (p.failed / (p.sent + p.failed)) * totalH : 0;
        return (
          <div
            key={p.date}
            className="flex-1 flex-col"
            title={`${p.date}: ${p.sent} sent, ${p.failed} failed`}
          >
            <div className="flex h-24 flex-col-reverse">
              <div
                style={{
                  height: `${totalH - failedH}%`,
                  background: darkTheme.colors.success,
                }}
              />
              <div
                style={{
                  height: `${failedH}%`,
                  background: darkTheme.colors.danger,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TypeBreakdownTable({ rows }: { rows: TypeBreakdown[] }): JSX.Element {
  return (
    <section>
      <SectionTitle>Pro Notification-Typ</SectionTitle>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-text-tertiary">
            <th className="pb-1 font-normal">Typ</th>
            <th className="pb-1 text-right font-normal">Sent</th>
            <th className="pb-1 text-right font-normal">Failed</th>
            <th className="pb-1 text-right font-normal">Skip</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.type}>
              <td className="py-0.5 text-text-primary">{row.type}</td>
              <td className="py-0.5 text-right tabular-nums text-text-primary">
                {row.sent.toLocaleString('de-DE')}
              </td>
              <td
                className="py-0.5 text-right tabular-nums"
                style={{
                  color:
                    row.failed > 0 ? darkTheme.colors.danger : darkTheme.colors.textPrimary,
                }}
              >
                {row.failed.toLocaleString('de-DE')}
              </td>
              <td className="py-0.5 text-right tabular-nums text-text-tertiary">
                {row.skipped.toLocaleString('de-DE')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function SkipsBreakdown({
  totals,
}: {
  totals: PushHealthDetails['totals'];
}): JSX.Element {
  const items = [
    { label: 'Quiet Hours', value: totals.skippedQuiet },
    { label: 'Pref-Toggle aus', value: totals.skippedPrefs },
    { label: 'Master aus', value: totals.skippedMaster },
    { label: 'Lernpause', value: totals.skippedPause ?? 0 },
    { label: 'Keine Tokens', value: totals.noTokens },
  ];
  return (
    <section>
      <SectionTitle>Skips</SectionTitle>
      <ul className="space-y-1 text-xs">
        {items.map((item) => (
          <li key={item.label} className="flex justify-between">
            <span className="text-text-secondary">{item.label}</span>
            <span className="tabular-nums text-text-primary">
              {item.value.toLocaleString('de-DE')}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Box({
  children,
  dangerous,
}: {
  children: React.ReactNode;
  dangerous?: boolean;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-4 text-xs"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: dangerous ? darkTheme.colors.danger : darkTheme.colors.borderSubtle,
        color: dangerous ? darkTheme.colors.danger : darkTheme.colors.textSecondary,
      }}
    >
      {children}
    </div>
  );
}
